import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

function WysiwygEditor({ value, onChange, placeholder = 'Enter description...' }) {
  const editorRef = useRef(null);
  const quillRef = useRef(null);

  useEffect(() => {
    // Prevent multiple initializations
    if (!editorRef.current || quillRef.current) return;

    // Clear any existing content
    editorRef.current.innerHTML = '';

    // Create Quill instance
    const quill = new Quill(editorRef.current, {
      theme: 'snow',
      placeholder: placeholder,
      modules: {
        toolbar: [
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline'],
          [{ 'align': [] }],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          ['link'],
          ['clean']
        ]
      },
      formats: [
        'header', 'bold', 'italic', 'underline',
        'align', 'list', 'link'
      ]
    });

    quillRef.current = quill;

    // Set initial content
    if (value) {
      // If value is plain text (no HTML tags), convert it to HTML
      const textContent = value.trim();
      if (textContent && !textContent.includes('<') && !textContent.includes('>')) {
        // Plain text - convert line breaks to paragraphs
        const paragraphs = textContent.split(/\n\s*\n/).filter(p => p.trim());
        if (paragraphs.length > 1) {
          quill.root.innerHTML = paragraphs.map(p => `<p>${p.trim()}</p>`).join('');
        } else {
          quill.root.innerHTML = `<p>${textContent}</p>`;
        }
      } else {
        // Already HTML
        quill.root.innerHTML = value;
      }
    }

    // Only bubble user edits — API/silent updates (e.g. syncing value into Quill) must not
    // call onChange or we fight React state and can wipe paste/content (especially with multiple editors).
    quill.on('text-change', (delta, oldDelta, source) => {
      if (source !== 'user') return;
      if (onChange) {
        onChange(quill.root.innerHTML);
      }
    });

    // Cleanup function
    return () => {
      if (quillRef.current) {
        quillRef.current = null;
      }
    };
  }, []); // Empty dependency array

  // Update content when value prop changes (e.g. load from API). Skip while this editor is focused
  // so Quill-normalized HTML vs parent string mismatches don't overwrite a paste mid-edit.
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) return;
    const root = quill.root;
    if (root.contains(document.activeElement)) return;
    if (value === root.innerHTML) return;

    const textContent = (value || '').trim();
    if (textContent && !textContent.includes('<') && !textContent.includes('>')) {
      const paragraphs = textContent.split(/\n\s*\n/).filter(p => p.trim());
      if (paragraphs.length > 1) {
        root.innerHTML = paragraphs.map(p => `<p>${p.trim()}</p>`).join('');
      } else {
        root.innerHTML = `<p>${textContent}</p>`;
      }
    } else {
      root.innerHTML = value || '';
    }
  }, [value]);

  return (
    <div className="wysiwyg-editor">
      <div ref={editorRef} className="quill-editor wysiwyg-quill-editor" />
    </div>
  );
}

export default WysiwygEditor;